"use client";

import {
  QueryClientProvider,
  QueryClient,
  QueryCache,
} from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Provider as JotaiProvider } from "jotai";
// import { AxiosError } from "axios";
import { useRouter } from "@/app/components/lib/navigation/useRouter";
import { useLogout } from "@/hooks/useLogout";
import {
  MAINTENANCE_EVENT,
  type MaintenanceEventDetail,
  companyErrorPath,
} from "./functions/maintenance";
import { PagesPaths } from "./functions/pages-paths";

/** Mirrors the flag in BaseClass.ts: one navigation per page lifetime, not one per query. */
let outOfSubscriptionDispatched = false;

function Providers({ children }: React.PropsWithChildren) {
  const router = useRouter();
  const { logout } = useLogout();
  const [client] = useState(
    // new QueryClient({ defaultOptions: { queries: { staleTime: Infinity } } })
    () =>
      new QueryClient({
        queryCache: new QueryCache({
          onError: (error: any) => {
            // if (["ERR_NETWORK"].includes(error.code!)) setNoInternet(true);
            // if (error?.response?.status === 406) {
            //   router.replace("/");
            //   setNoInternet(true);
            // }
            // if (["ERR_BAD_REQUEST"].includes(error.code)) router.reload();
            if (error?.response?.status === 401) {
              // Only logout if the 401 came from the refresh-token endpoint
              // (meaning the refresh itself failed, not just the access token expired)
              const requestUrl =
                error?.config?.url ||
                error?.response?.config?.url ||
                "";
              if (requestUrl.includes("/users/refresh-token")) {
                logout();
                router.replace(PagesPaths.signin);
              }
            }
            // Belt-and-braces for a mid-session lapse that somehow bypassed the axios
            // interceptor branch in BaseClass.ts (e.g. a query using plain fetch).
            // Guarded locally: BaseClass's module flag is not visible from here, so
            // without this every failing query in the batch would fire its own event.
            const status = error?.response?.status;
            if (
              (status === 430 || status === 431) &&
              !outOfSubscriptionDispatched
            ) {
              outOfSubscriptionDispatched = true;
              window.dispatchEvent(
                new CustomEvent("company:out-of-subscription"),
              );
            }
          },
        }),
        defaultOptions: {
          queries: {
            staleTime: Infinity,
            // Inactive queries must be collectable. With `cacheTime: Infinity` every product
            // list, filter result and search the user visited was retained for the whole
            // session — a measured 71MB heap after a single scroll, which is where Android
            // starts killing background tabs.
            cacheTime: 5 * 60_000,
            // One retry, not react-query's default of three. With several queries per product
            // card, a backend blip otherwise becomes a retry storm (each with a CORS preflight)
            // competing with the LCP image for bandwidth.
            retry: 1,
            retryDelay: (attempt: number) => Math.min(1000 * 2 ** attempt, 8000),
            // On mobile, "window focus" fires every time the user returns from another app —
            // far too often to refetch everything.
            refetchOnWindowFocus: false,
            refetchOnReconnect: true, // a genuine reconnect IS a good reason to refetch
          },
        },
      }),
  );

  // Listen for token deletion events from the axios interceptor (BaseClass.ts)
  useEffect(() => {
    const handleTokensDeleted = () => {
      logout();
      router.replace(PagesPaths.signin);
    };
    window.addEventListener("auth:tokens-deleted", handleTokensDeleted);
    return () =>
      window.removeEventListener("auth:tokens-deleted", handleTokensDeleted);
  }, [logout, router]);

  // The tenant's subscription lapsed mid-session (dispatched from BaseClass.ts). Navigate
  // once instead of letting every queued query fail on its own.
  //
  // A HARD navigation, deliberately — not the wrapped router:
  //  1. `useRouter` here prefixes the current locale, producing `/en/company-error`, which
  //     does not exist (the route lives outside `[lang]`) and would 404.
  //  2. `/company-error` renders its own <html>/<body>, which a soft navigation cannot
  //     swap cleanly.
  //  3. It tears down every in-flight request and forces a fresh server render — which is
  //     what records the verdict in Redis.
  useEffect(() => {
    const handleOutOfSubscription = () => {
      window.location.assign(PagesPaths.companyError);
    };
    window.addEventListener(
      "company:out-of-subscription",
      handleOutOfSubscription,
    );
    return () =>
      window.removeEventListener(
        "company:out-of-subscription",
        handleOutOfSubscription,
      );
  }, [router]);

  // The backend went into maintenance after this page loaded (dispatched from
  // BaseClass.ts). Hard navigation for the same reasons as above; the Retry-After rides
  // along because the browser saw the 503 directly and the server may not know yet.
  useEffect(() => {
    const handleMaintenance = (event: Event) => {
      const { retryAfterSeconds } = (event as CustomEvent<MaintenanceEventDetail>)
        .detail;
      window.location.assign(companyErrorPath(retryAfterSeconds));
    };
    window.addEventListener(MAINTENANCE_EVENT, handleMaintenance);
    return () =>
      window.removeEventListener(MAINTENANCE_EVENT, handleMaintenance);
  }, []);

  return (
    <JotaiProvider>
      <QueryClientProvider client={client}>{children}</QueryClientProvider>
    </JotaiProvider>
  );
}

export default Providers;
